import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedCollapse } from '@/components/ui/animated-collapse';
import { cn } from '@/lib/utils';
import { EASE_EXPO_OUT, DURATION_FAST, DURATION_MEDIUM } from '@/lib/motion';
import { Zap, Settings, ChevronDown } from 'lucide-react';
import {
  Badge,
  Card,
  Button,
  HelpButton
} from '@/components/ui';
import { toast } from '@/components/ui/sonner';
import { LabeledToggle } from '@/components/config_forms/shared/LabeledToggle';

import { type BillingStructure, type RateMultiplier } from '@/types';
import type { RoleInfo } from './multiplierUtils';

const DAY_NAMES: Record<number, string> = {
  1: 'Monday', 2: 'Tuesday', 3: 'Wednesday', 4: 'Thursday',
  5: 'Friday', 6: 'Saturday', 7: 'Sunday',
};

import { MultiplierList } from './MultiplierList';
import { MultiplierDetailSheet } from './MultiplierDetailSheet';
import { WorkWeekConfigForm } from '@/components/config_forms/multipliers/WorkWeekConfigForm';
import { fieldDescriptions } from './utils/loadDescriptions';
import { fieldLabelClass } from '@/components/config_forms/shared/surcharge/fieldStyles';
import { decimalToHHMM, isStraightTimeMultiplier, getSuggestedDayRateThreshold } from './multiplierUtils';

export type OvertimeConcurrencyStrategy = 'highest_prevailing' | 'combine_premiums';
export type DayRateCalculationBasis = 'billable' | 'actual';

export interface WorkWeekProps {
  weekStart: number | null | undefined;
  onWeekStartChange: (value: number) => void;
  weekStartError?: React.ReactNode;
  overtimeConcurrencyStrategy: OvertimeConcurrencyStrategy | null | undefined;
  onOvertimeConcurrencyStrategyChange: (value: OvertimeConcurrencyStrategy) => void;
  perWeekDayRateCalculationBasis: DayRateCalculationBasis | null | undefined;
  onPerWeekDayRateCalculationBasisChange: (value: DayRateCalculationBasis) => void;
}

export interface MultipliersConfigFormProps extends WorkWeekProps {
  rateMultipliers: RateMultiplier[];
  onMultipliersChange: (newMultipliers: RateMultiplier[]) => void;
  billingStructure: BillingStructure | null;
  minimumBillableHours: number | null;
  isCustomized?: boolean;
  onRevert?: () => void;
  noCard?: boolean;
  title?: string;
  description?: string;
  onRemove: (index: number) => void;
  context?: 'user' | 'client';
  parentState?: { roles?: RoleInfo[] };
}

const MultipliersConfigFormComponent: React.FC<MultipliersConfigFormProps> = ({
  rateMultipliers,
  onMultipliersChange,
  billingStructure,
  minimumBillableHours,
  isCustomized,
  onRevert,
  noCard,
  title = "Overtime Multipliers",
  description = "Set your overtime rate multiplier progression.",
  onRemove,
  weekStart,
  onWeekStartChange,
  weekStartError,
  context = 'user',
  overtimeConcurrencyStrategy,
  onOvertimeConcurrencyStrategyChange,
  perWeekDayRateCalculationBasis,
  onPerWeekDayRateCalculationBasisChange,
  parentState: _parentState,
}) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<'create' | 'edit'>('create');
  const [sheetMultiplierIndex, setSheetMultiplierIndex] = useState<number>(0);
  const [liveAddThreshold, setLiveAddThreshold] = useState<number | null>(null);
  const [committedAddThreshold, setCommittedAddThreshold] = useState<number | null>(null);
  const [liveEditValues, setLiveEditValues] = useState<import('./multiplierUtils').MultiplierLiveValues | null>(null);

  const hasWeeklyInMultipliers = rateMultipliers.some(
    (m) =>
      m.defaultPerWeekThresholdHours != null &&
      m.defaultPerWeekThresholdHours > 0
  );
  const workWeekConfigActive = hasWeeklyInMultipliers;

  // Derive effective billing structure from live roles when available,
  // falling back to the (potentially stale) billingStructure prop
  const effectiveBillingStructure = (() => {
    const roles = _parentState?.roles;
    if (!roles?.length) return billingStructure;
    const allDayRate = roles.every(r => r.billingStructure === 'day_rate');
    const allHourly = roles.every(r => r.billingStructure === 'hourly');
    if (allDayRate) return 'day_rate' as const;
    if (allHourly) return 'hourly' as const;
    return null; // mixed
  })();

  const hasDayRateRoles = effectiveBillingStructure !== 'hourly';
  const usesWeeklyForDayRates = rateMultipliers.some((m) => {
    if (m.defaultPerWeekThresholdHours == null) return false;
    // If all roles share the same billing structure (uniform), the multiplier
    // implicitly applies to that structure even if perWeekBillingStructures is null
    if (effectiveBillingStructure != null) return effectiveBillingStructure === 'day_rate';
    return m.perWeekBillingStructures?.includes('day_rate') ?? false;
  });
  const accumulationMethodActive = workWeekConfigActive && hasDayRateRoles && usesWeeklyForDayRates;

  const hasDailyInMultipliers = rateMultipliers.some(
    (m) =>
      m.defaultPerShiftThresholdHours != null &&
      m.defaultPerShiftThresholdHours > 0
  );
  const overtimeStrategyActive = workWeekConfigActive && hasDailyInMultipliers;

  const isMobile = useIsMobile();

  // Accordion states
  const showAccordionHeader = !noCard || context === 'user';
  const [multipliersOpen, setMultipliersOpen] = useState(true);
  const [overtimeSettingsOpen, setOvertimeSettingsOpen] = useState(false);

  // Refs for auto-scroll on expand (skip initial render to avoid jarring scroll on mount)
  const multipliersHeaderRef = useRef<HTMLButtonElement>(null);
  const settingsHeaderRef = useRef<HTMLButtonElement>(null);
  const didMountRef = useRef(false);

  useEffect(() => {
    if (!didMountRef.current) { didMountRef.current = true; return; }
    if (multipliersOpen && multipliersHeaderRef.current) {
      multipliersHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [multipliersOpen]);

  useEffect(() => {
    if (overtimeSettingsOpen && settingsHeaderRef.current) {
      settingsHeaderRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [overtimeSettingsOpen]);

  // Collapsed summary for Overtime Multipliers section (excludes the base Straight Time multiplier)
  const multiplierSummary = useMemo(() => {
    const nonStraightTime = rateMultipliers.filter(m => !isStraightTimeMultiplier(m.code));
    if (nonStraightTime.length === 0) return null;
    const sorted = [...rateMultipliers].sort((a, b) => {
      const aIsST = isStraightTimeMultiplier(a.code);
      const bIsST = isStraightTimeMultiplier(b.code);
      if (aIsST && !bIsST) return -1;
      if (!aIsST && bIsST) return 1;
      return (a.defaultPerShiftThresholdHours ?? 0) - (b.defaultPerShiftThresholdHours ?? 0);
    });
    const chain = sorted.map(m => {
      if (isStraightTimeMultiplier(m.code)) return '1x';
      const threshold = m.defaultPerShiftThresholdHours ?? m.defaultPerWeekThresholdHours;
      if (threshold == null) return `${m.defaultMultiplier}x`;
      const suffix = !m.defaultPerShiftThresholdHours && m.defaultPerWeekThresholdHours
        ? '/wk' : '';
      return `${m.defaultMultiplier}x after ${decimalToHHMM(threshold)}h${suffix}`;
    }).join(' → ');
    const shortChain = sorted.map(m => {
      if (isStraightTimeMultiplier(m.code)) return '1x';
      return `${m.defaultMultiplier}x`;
    }).join(' → ');
    const hasShift = nonStraightTime.some(m => m.defaultPerShiftThresholdHours != null);
    const hasWeekly = nonStraightTime.some(m => m.defaultPerWeekThresholdHours != null);
    return { chain, shortChain, hasShift, hasWeekly };
  }, [rateMultipliers]);

  // Accordion header summary: always show all relevant fields and their current selection
  const overtimeSummaryTokens: { label: string; value: string }[] = [];
  overtimeSummaryTokens.push({ label: 'Week Start', value: DAY_NAMES[weekStart ?? 1]?.slice(0, 3) ?? 'Mon' });
  if (accumulationMethodActive) {
    overtimeSummaryTokens.push({ label: 'Hours Count', value: perWeekDayRateCalculationBasis === 'billable' ? 'Min Billable' : 'Actual' });
  }
  if (overtimeStrategyActive) {
    overtimeSummaryTokens.push({ label: 'Overtime Overlap', value: overtimeConcurrencyStrategy === 'combine_premiums' ? 'Stacked' : 'Higher Rate' });
  }

  // Auto-expand Overtime Settings when weekly thresholds first activate
  const prevWorkWeekActive = useRef(workWeekConfigActive);
  useEffect(() => {
    if (workWeekConfigActive && !prevWorkWeekActive.current) {
      setOvertimeSettingsOpen(true);
    }
    prevWorkWeekActive.current = workWeekConfigActive;
  }, [workWeekConfigActive]);

  // Derive rate context from first role
  const firstRole = _parentState?.roles?.[0];
  const dayRate = firstRole?.rate ?? null;
  const primaryRoleName = firstRole?.name ?? null;
  const shortShiftHours = minimumBillableHours ? Math.floor(minimumBillableHours * 0.6) : 0;

  const handleWeekStartChangeWithToast = (value: number) => {
    if (value !== weekStart) {
      onWeekStartChange(value);
      toast.success(`Work week starts ${DAY_NAMES[value]}`);
    }
  };

  const handleEdit = (index: number) => {
    if (sheetOpen && sheetMultiplierIndex === index && sheetMode === 'edit') {
      setSheetOpen(false);
      setLiveEditValues(null);
      return;
    }
    (document.activeElement as HTMLElement)?.blur?.();
    setLiveEditValues(null);
    setSheetMultiplierIndex(index);
    setSheetMode('edit');
    setSheetOpen(true);
  };

  const handleAdd = () => {
    // Blur trigger so Radix Sheet's aria-hidden on the page doesn't conflict with focused element
    (document.activeElement as HTMLElement)?.blur?.();
    setSheetMode('create');
    setSheetMultiplierIndex(-1);
    // Set initial committed threshold for sort position before the form opens
    const roles = _parentState?.roles ?? [];
    const suggestion = getSuggestedDayRateThreshold(roles, minimumBillableHours, rateMultipliers);
    setCommittedAddThreshold(suggestion);
    setSheetOpen(true);
  };

  const handleSheetSave = (multiplier: RateMultiplier) => {
    if (sheetMode === 'create') {
      const snapshot = rateMultipliers;
      onMultipliersChange([...snapshot, multiplier]);
      const thresholdDesc = multiplier.defaultPerShiftThresholdHours
        ? ` after ${decimalToHHMM(multiplier.defaultPerShiftThresholdHours)}h`
        : '';
      toast.success(`${multiplier.name} (${multiplier.defaultMultiplier}x) added${thresholdDesc}`, {
        duration: 5000,
        action: {
          label: 'Undo',
          onClick: () => {
            onMultipliersChange(snapshot);
            toast('Multiplier removed');
          },
        },
      });
    } else {
      const updated = [...rateMultipliers];
      const idx = updated.findIndex(m => m.code === multiplier.code);
      if (idx !== -1) {
        updated[idx] = multiplier;
      } else {
        updated.push(multiplier);
      }
      onMultipliersChange(updated);
    }
    setSheetOpen(false);
    setLiveAddThreshold(null);
    setLiveEditValues(null);
  };

  const content = (
    <div className={cn("space-y-4 md:space-y-6 lg:max-w-3xl")}>
      {showAccordionHeader && (
        <div>
          <button
            ref={multipliersHeaderRef}
            type="button"
            onClick={() => setMultipliersOpen(prev => {
              if (prev) setSheetOpen(false); // close inline sheet before collapsing
              return !prev;
            })}
            aria-expanded={multipliersOpen}
            className="w-full flex items-center justify-between py-1 group"
          >
            <div className="flex items-center gap-3">
              <Zap className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              <h2 className="font-medium text-base text-foreground">{title}</h2>
              {isCustomized && <Badge variant="default">{context === 'client' ? 'Client' : 'Profile'} Override</Badge>}
            </div>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform duration-200',
                multipliersOpen && 'rotate-180'
              )}
            />
          </button>
          {multipliersOpen && (
            <p className="text-sm text-muted-foreground mt-1">
              Define your rate progression and when overtime kicks in — per shift, per week, or both.
            </p>
          )}
          {multipliersOpen && isCustomized && onRevert && (
            <Button variant="link" onClick={onRevert} className="text-sm h-auto p-0 mt-1">
              Revert to Profile Defaults
            </Button>
          )}
        </div>
      )}

      <AnimatedCollapse open={multipliersOpen}>
        <div className="border border-border rounded bg-card dark:bg-transparent shadow-sm">
          <div className="grid grid-cols-[auto_1fr_auto] items-center px-3 py-2.5 min-h-[44px] bg-muted border-b-2 border-b-border">
            <div className="flex items-center gap-2">
              <Zap className="w-3.5 h-3.5 text-purple-600 dark:text-purple-400 flex-shrink-0" />
              <span className="font-heading text-sm font-semibold uppercase tracking-wide text-foreground">
                Overtime Ladder
              </span>
            </div>
            {multiplierSummary != null && (
              <span className="text-[11px] sm:text-[12px] font-mono text-muted-foreground text-center truncate px-2">
                <span className="sm:hidden">{multiplierSummary.shortChain}</span>
                <span className="hidden sm:inline">{multiplierSummary.chain}</span>
              </span>
            )}
            {multiplierSummary != null && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                {multiplierSummary.hasShift && (
                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] sm:text-[12px] font-mono font-semibold leading-none bg-primary/25 dark:bg-primary/20 text-primary-text" aria-label="per shift"><span className="text-[9px]" aria-hidden="true">⏱</span><span className="hidden sm:inline">per shift</span></span>
                )}
                {multiplierSummary.hasWeekly && (
                  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] sm:text-[12px] font-mono font-semibold leading-none bg-purple-500/25 text-purple-700 dark:bg-purple-500/20 dark:text-purple-400" aria-label="weekly"><span className="text-[9px]" aria-hidden="true">📅</span><span className="hidden sm:inline">weekly</span></span>
                )}
              </div>
            )}
          </div>
          <div className="px-3 pt-3 pb-1 space-y-4">
            <p className="text-[12px] sm:text-[13px] text-muted-foreground hidden sm:block">
              {description}
            </p>
          <MultiplierList
            multipliers={rateMultipliers}
            onEdit={handleEdit}
            onAdd={handleAdd}
            expandedIndex={!isMobile && sheetOpen ? (sheetMode === 'edit' ? sheetMultiplierIndex : -1) : null}
            liveAddThreshold={sheetMode === 'create' ? liveAddThreshold : undefined}
            committedAddThreshold={sheetMode === 'create' ? committedAddThreshold : undefined}
            liveEditValues={sheetMode === 'edit' ? liveEditValues : undefined}
            inlineForm={!isMobile && sheetOpen ? (
              <MultiplierDetailSheet
                open={sheetOpen}
                onOpenChange={setSheetOpen}
                mode={sheetMode}
                multiplier={sheetMode === 'edit' ? rateMultipliers[sheetMultiplierIndex] : undefined}
                existingMultipliers={rateMultipliers}
                billingStructure={billingStructure}
                minimumBillableHours={minimumBillableHours}
                roles={_parentState?.roles}
                dayRate={dayRate}
                primaryRoleName={primaryRoleName}
                onSave={handleSheetSave}
                onDelete={onRemove ? (m) => {
                  const idx = rateMultipliers.findIndex(r => r.code === m.code);
                  if (idx > -1) onRemove(idx);
                } : undefined}
                onLiveThresholdChange={sheetMode === 'create' ? setLiveAddThreshold : undefined}
                onCommittedThresholdChange={sheetMode === 'create' ? setCommittedAddThreshold : undefined}
                onLiveValuesChange={sheetMode === 'edit' ? setLiveEditValues : undefined}
              />
            ) : undefined}
          />
          </div>

          {/* Overtime Settings — collapsible accordion */}
          <AnimatedCollapse open={workWeekConfigActive}>
                <div className="border-t border-border/40 mt-3">
                  {/* Accordion header */}
                  <button
                    ref={settingsHeaderRef}
                    type="button"
                    onClick={() => setOvertimeSettingsOpen(prev => !prev)}
                    aria-expanded={overtimeSettingsOpen}
                    className={cn(
                      'w-full flex items-center justify-between px-3 py-2.5 min-h-[44px]',
                      'bg-muted hover:bg-muted/80 transition-colors cursor-pointer',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset',
                      overtimeSettingsOpen && 'border-b-2 border-b-border'
                    )}
                  >
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <Settings className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="font-heading text-sm font-semibold uppercase tracking-wide text-foreground">
                        Overtime Settings
                      </span>
                    </div>
                    <span className={cn(
                      "text-[11px] sm:text-[12px] font-mono truncate text-center flex-1 px-2",
                      overtimeSettingsOpen ? "text-muted-foreground/50" : "text-muted-foreground"
                    )}>
                      <AnimatePresence mode="popLayout">
                        {overtimeSummaryTokens.map((t) => (
                          <motion.span
                            key={t.label}
                            initial={{ opacity: 0, scale: 0.85 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.85 }}
                            transition={{ duration: DURATION_MEDIUM, ease: EASE_EXPO_OUT }}
                            className="inline-flex whitespace-nowrap origin-left"
                          >
                            <span className="text-muted-foreground/30 mx-1.5">·</span>
                            <span className="text-muted-foreground/50">{t.label}:{' '}</span>
                            <AnimatePresence mode="popLayout" initial={false}>
                              <motion.span
                                key={t.value}
                                initial={{ opacity: 0, y: -4 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: 4 }}
                                transition={{ duration: DURATION_FAST, ease: EASE_EXPO_OUT }}
                                className="text-foreground/70"
                              >
                                {t.value}
                              </motion.span>
                            </AnimatePresence>
                          </motion.span>
                        ))}
                      </AnimatePresence>
                    </span>
                    <ChevronDown
                      className={cn(
                        'w-4 h-4 text-muted-foreground transition-transform duration-200 flex-shrink-0',
                        overtimeSettingsOpen && 'rotate-180'
                      )}
                    />
                  </button>

                  {/* Accordion body */}
                  <AnimatedCollapse open={overtimeSettingsOpen}>
                        <div className="p-3 space-y-3 sm:space-y-4">
                          <p className="text-[12px] sm:text-[13px] text-muted-foreground">
                            Controls how weekly hour thresholds (📅) interact.
                          </p>

                          <WorkWeekConfigForm
                            weekStart={weekStart}
                            onWeekStartChange={handleWeekStartChangeWithToast}
                            error={weekStartError}
                            isCustomized={isCustomized ?? false}
                            onRevert={() => {}}
                            isLoading={!workWeekConfigActive}
                            integrated={true}
                          />

                          <AnimatedCollapse open={accumulationMethodActive || overtimeStrategyActive}>
                                <div className="space-y-4">
                                  {accumulationMethodActive && (
                                    <div
                                      data-testid="weekly-hours-count-field"
                                      className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 items-center sm:flex sm:items-center sm:gap-4"
                                    >
                                      <div className="min-w-0 leading-none sm:flex-1">
                                        <span className={cn(fieldLabelClass, "align-middle")}>Weekly Hours Count</span>
                                        <HelpButton
                                          fieldName="Weekly Hours Count"
                                          content={fieldDescriptions.hours_accumulation_method}
                                          variables={{ billingStructure, Math }}
                                          enableFieldFocus
                                          className="align-middle ml-4"
                                        />
                                        <p className="hidden sm:block sm:mt-2 leading-snug text-[12px] sm:text-[13px] text-muted-foreground">
                                          {minimumBillableHours ? (
                                            <>If you work {shortShiftHours}hrs on a {minimumBillableHours}hr day rate, count {shortShiftHours} (actual) or {minimumBillableHours} (minimum) toward the weekly total?</>
                                          ) : (
                                            'Count actual hours worked or your day rate minimum toward the weekly total?'
                                          )}
                                        </p>
                                      </div>
                                      <div className="flex flex-col items-start gap-1 sm:items-end sm:flex-shrink-0">
                                        <LabeledToggle
                                          ariaLabel="Weekly Hours Count"
                                          leftLabel="Min Billable"
                                          rightLabel="Actual Hrs"
                                          rightSelected={(perWeekDayRateCalculationBasis ?? 'billable') === 'actual'}
                                          onChange={(rightSelected) =>
                                            onPerWeekDayRateCalculationBasisChange(rightSelected ? 'actual' : 'billable')
                                          }
                                        />
                                      </div>
                                      <p className="col-span-2 sm:hidden text-[12px] sm:text-[13px] text-muted-foreground">
                                        {minimumBillableHours ? (
                                          <>If you work {shortShiftHours}hrs on a {minimumBillableHours}hr day rate, count {shortShiftHours} (actual) or {minimumBillableHours} (minimum) toward the weekly total?</>
                                        ) : (
                                          'Count actual hours worked or your day rate minimum toward the weekly total?'
                                        )}
                                      </p>
                                    </div>
                                  )}

                                  {overtimeStrategyActive && (
                                    <div
                                      data-testid="overlapping-overtime-field"
                                      className="w-full grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-2 items-center border-t border-border/40 pt-4 sm:flex sm:items-center sm:gap-4"
                                    >
                                      <div className="min-w-0 leading-none sm:flex-1">
                                        <span className={cn(fieldLabelClass, "align-middle")}>Overlapping Overtime</span>
                                        <HelpButton
                                          fieldName="Overlapping Overtime"
                                          content={fieldDescriptions.overtime_concurrency_strategy}
                                          variables={{ billingStructure, Math }}
                                          enableFieldFocus
                                          className="align-middle ml-4"
                                        />
                                        <p className="hidden sm:block sm:mt-2 leading-snug text-[12px] sm:text-[13px] text-muted-foreground">
                                          When a shift hits both daily and weekly overtime, use the higher rate or add them together?
                                        </p>
                                      </div>
                                      <div className="flex flex-col items-start gap-1 sm:items-end sm:flex-shrink-0">
                                        <LabeledToggle
                                          ariaLabel="Overlapping Overtime"
                                          leftLabel="Higher Rate"
                                          rightLabel="Stack Both"
                                          rightSelected={(overtimeConcurrencyStrategy ?? 'highest_prevailing') === 'combine_premiums'}
                                          onChange={(rightSelected) =>
                                            onOvertimeConcurrencyStrategyChange(rightSelected ? 'combine_premiums' : 'highest_prevailing')
                                          }
                                          disabled={!overtimeStrategyActive}
                                        />
                                      </div>
                                      <p className="col-span-2 sm:hidden text-[12px] sm:text-[13px] text-muted-foreground">
                                        When a shift hits both daily and weekly overtime, use the higher rate or add them together?
                                      </p>
                                    </div>
                                  )}
                                </div>
                          </AnimatedCollapse>
                        </div>
                  </AnimatedCollapse>
                </div>
          </AnimatedCollapse>
        </div>

        {/* Mobile: Sheet overlay for main ladder (portals to document root) */}
        {isMobile && (
          <MultiplierDetailSheet
            open={sheetOpen}
            onOpenChange={setSheetOpen}
            mode={sheetMode}
            multiplier={sheetMode === 'edit' ? rateMultipliers[sheetMultiplierIndex] : undefined}
            existingMultipliers={rateMultipliers}
            billingStructure={billingStructure}
            minimumBillableHours={minimumBillableHours}
            roles={_parentState?.roles}
            dayRate={dayRate}
            primaryRoleName={primaryRoleName}
            onSave={handleSheetSave}
            onDelete={onRemove ? (m) => {
              const idx = rateMultipliers.findIndex(r => r.code === m.code);
              if (idx > -1) onRemove(idx);
            } : undefined}
          />
        )}

      </AnimatedCollapse>

    </div>
  );

  if (noCard) {
    return content;
  }

  return (
    <Card
      id="multipliers-step"
      className={cn(
        "animate-fade-in shadow-sm rounded-xl",
        "p-3 pb-4 md:p-6 md:max-w-3xl md:mx-auto",
        // Removed the border highlight as per request
      )}
    >
      {content}
    </Card>
  );
};

export const MultipliersConfigForm = React.memo(MultipliersConfigFormComponent);
MultipliersConfigForm.displayName = 'MultipliersConfigForm';
